import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  BackButton,
  Card,
  CardBody,
  CardHeader,
  CardIcon,
  CardTitle,
  Grid,
  Header,
  Row,
  RowLabel,
  RowValue,
  StatusDot,
  Summary,
  SummaryChip,
  Title,
  Wrapper
} from './Diagnostics.styles'

const Diagnostics = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [uptime, setUptime] = useState(0)

  useEffect(() => {
    const start = Date.now()
    const interval = setInterval(() => {
      setUptime(Math.floor((Date.now() - start) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const formatUptime = (s) => {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return t('diagnostics.uptime', { h, m, s: sec })
  }

  const diagnosticData = [
    {
      id: 'device',
      icon: <DeviceIcon />,
      items: [
        { id: 'model', value: 'Elo Z30 POS' },
        { id: 'display', value: t('diagnostics.formats.display') },
        { id: 'touch', value: t('diagnostics.values.touch') },
        { id: 'os', value: 'Android (EloView)' },
        { id: 'uptime', value: formatUptime(uptime), live: true },
      ],
    },
    {
      id: 'network',
      icon: <NetworkIcon />,
      items: [
        { id: 'status', value: t('diagnostics.values.connected'), status: 'ok' },
        { id: 'type', value: t('diagnostics.values.ethernet') },
        { id: 'ip', value: '192.168.1.42' },
        { id: 'gateway', value: '192.168.1.1' },
        { id: 'dns', value: '8.8.8.8' },
        { id: 'latency', value: '2 ms', status: 'ok' },
      ],
    },
    {
      id: 'peripherals',
      icon: <PlugIcon />,
      items: [
        { id: 'scanner', value: t('diagnostics.values.connected'), status: 'ok' },
        { id: 'printer', value: t('diagnostics.values.ready'), status: 'ok' },
        { id: 'cashDrawer', value: t('diagnostics.values.closed'), status: 'ok' },
        { id: 'cardTerminal', value: t('diagnostics.values.connected'), status: 'ok' },
        { id: 'customerDisplay', value: t('diagnostics.values.notDetected'), status: 'warn' },
      ],
    },
    {
      id: 'software',
      icon: <GearIcon />,
      items: [
        { id: 'appVersion', value: '1.0.0' },
        { id: 'apiEndpoint', value: 'api.bergregions.pl' },
        { id: 'apiStatus', value: t('diagnostics.values.healthy'), status: 'ok' },
        { id: 'lastSync', value: t('diagnostics.values.minAgo', { count: 2 }), status: 'ok' },
        { id: 'license', value: t('diagnostics.values.active'), status: 'ok' },
      ],
    },
    {
      id: 'health',
      icon: <PulseIcon />,
      items: [
        { id: 'cpu', value: '23%', status: 'ok' },
        { id: 'memory', value: t('diagnostics.formats.memory'), status: 'ok' },
        { id: 'disk', value: t('diagnostics.values.diskFree', { size: 28 }), status: 'ok' },
        { id: 'temperature', value: '42°C', status: 'ok' },
        { id: 'battery', value: t('diagnostics.values.acPower'), status: 'ok' },
      ],
    },
    {
      id: 'security',
      icon: <LockIcon />,
      items: [
        { id: 'tls', value: t('diagnostics.values.tlsEnabled', { version: '1.3' }), status: 'ok' },
        { id: 'pci', value: t('diagnostics.values.compliant'), status: 'ok' },
        { id: 'firewall', value: t('diagnostics.values.active'), status: 'ok' },
        { id: 'lastUpdate', value: t('diagnostics.formats.lastUpdate') },
      ],
    },
  ]

  const statusCounts = diagnosticData
    .flatMap(section => section.items)
    .reduce((acc, item) => (item.status ? { ...acc, [item.status]: (acc[item.status] || 0) + 1 } : acc), {})

  return (
    <Wrapper>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowLeftIcon />
          {t('diagnostics.back')}
        </BackButton>
        <Title>{t('diagnostics.title')}</Title>
        <Summary>
          {statusCounts.ok > 0 && (
            <SummaryChip status="ok">
              <StatusDot status="ok" />
              {t('diagnostics.summary.ok', { count: statusCounts.ok })}
            </SummaryChip>
          )}
          {statusCounts.warn > 0 && (
            <SummaryChip status="warn">
              <StatusDot status="warn" />
              {t('diagnostics.summary.warning', { count: statusCounts.warn })}
            </SummaryChip>
          )}
        </Summary>
      </Header>
      <Grid>
        {diagnosticData.map(section => (
          <Card key={section.id}>
            <CardHeader>
              <CardIcon>{section.icon}</CardIcon>
              <CardTitle>{t(`diagnostics.sections.${section.id}`)}</CardTitle>
            </CardHeader>
            <CardBody>
              {section.items.map(item => (
                <Row key={item.id}>
                  <RowLabel>{t(`diagnostics.labels.${item.id}`)}</RowLabel>
                  <RowValue status={item.status}>
                    {item.status && <StatusDot status={item.status} />}
                    {item.value}
                  </RowValue>
                </Row>
              ))}
            </CardBody>
          </Card>
        ))}
      </Grid>
    </Wrapper>
  )
}

export default Diagnostics

// Line icons (inherit currentColor)

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
)

const ArrowLeftIcon = () => (
  <Icon>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Icon>
)

const DeviceIcon = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M9 20h6M12 16v4" />
  </Icon>
)

const NetworkIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
  </Icon>
)

const PlugIcon = () => (
  <Icon>
    <path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0V8zM12 17v4" />
  </Icon>
)

const GearIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" />
  </Icon>
)

const PulseIcon = () => (
  <Icon>
    <path d="M3 12h4l2.5-6 5 12 2.5-6h4" />
  </Icon>
)

const LockIcon = () => (
  <Icon>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </Icon>
)
